// Pure helpers for the Tracks tab: ordering, strip layout, selection ranges and undo bookkeeping.
// Characters come from the characters slice: { char_id, char_name, char_actor, rep_track_id, tracks_data }
// and each track is { track_id, start_time, end_time, thumbnail?, similarity? }.
import { screenTime, fmt, memoize } from "../scenes/sceneModel";

export { screenTime, fmt, memoize };

export const TILE = 64; // track tile, px
export const GAP = 6; // between tiles
export const PITCH = TILE + GAP;
export const STRIP_PAD = 3; // room for the 3px selection ring around a tile
export const GRID_LINES = 4; // an expanded character shows 4 lines of tracks, then scrolls
export const LOW_MATCH = 0.6; // similarity under this is flagged in red
// char_id -1 is the "Others" bucket of unassigned faces: moving tracks there is how a track is deleted,
// and moving them back to their characters undoes it
export const DELETED = -1;
export const isOther = (c) => !!c && c.char_id === DELETED;

// names the backend gives characters nobody has named yet
const DEFAULT_NAME = /^(character|speaker|unknown|personnage|locuteur|inconnu)\s*#?\s*\d*$/i;
export const isUnnamed = (name) => !name || DEFAULT_NAME.test(String(name).trim());

/** tiles that fit on one line of a strip whose inner width is `width` px (at least 2: a track and "+N more") */
export function perLine(width) {
  return Math.max(2, Math.floor((Math.max(0, width) + GAP) / PITCH));
}

/**
 * one-line strip: `shown` tracks, then a "+`more`" tile when they don't all fit
 * @param n number of tracks
 * @param per tiles per line
 */
export function collapsedLayout(n, per) {
  if (n <= per) return { shown: n, more: 0 };
  return { shown: per - 1, more: n - (per - 1) };
}

/** expanded grid: every track plus a "Less" tile, `per` to a line; at most GRID_LINES lines tall, then it scrolls */
export function expandedLayout(n, per) {
  const lines = Math.max(1, Math.ceil((n + 1) / per));
  return { lines, height: Math.min(lines, GRID_LINES) * PITCH, lessIndex: n };
}

/** does the backend send a similarity score (0-1, how closely a track's face matches its character)? */
export const hasSimilarity = (items) => (items || []).some((c) => (c.tracks_data || []).some((t) => typeof t.similarity === "number"));

const similarity = (t) => (typeof t.similarity === "number" ? t.similarity : 1);
const byStart = (a, b) => a.start_time - b.start_time || (a.track_id < b.track_id ? -1 : a.track_id > b.track_id ? 1 : 0);

/** tracks in display order: by start time, or least similar to the character first */
export function orderTracks(tracks, mode) {
  const list = (tracks || []).slice();
  list.sort(mode === "similar" ? (a, b) => similarity(a) - similarity(b) || byStart(a, b) : byStart);
  return list;
}

/** ids from `a` to `b` inclusive, in the order of `ids` (shift-click and drag-across ranges) */
export function rangeIds(ids, a, b) {
  const i = ids.indexOf(a),
    j = ids.indexOf(b);
  if (i < 0 || j < 0) return j < 0 ? [] : [b];
  return ids.slice(Math.min(i, j), Math.max(i, j) + 1);
}

/** characters matching the search text (name or actor), case- and accent-insensitive */
const fold = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
export function filterCharacters(items, q) {
  const k = fold(q).trim();
  if (!k) return items || [];
  return (items || []).filter((c) => fold(c.char_name).includes(k) || fold(c.char_actor).includes(k));
}

export const SORTS = { screen: "Screen time", tracks: "Track count", name: "Name" };

/**
 * characters in list order, the Others bucket last; `pinned` ids (just created) stay on top until the page reloads
 * @param sort "screen" | "tracks" | "name"
 */
export function sortCharacters(items, sort, pinned) {
  const pin = pinned || [];
  const rank = (c) => {
    const i = pin.indexOf(c.char_id);
    return i < 0 ? pin.length : i;
  };
  const screen = new Map((items || []).map((c) => [c.char_id, screenTime(c.tracks_data)]));
  // by name: named characters first, numbers in names in number order (Character 3 before Character 10)
  const cmp =
    sort === "name"
      ? (a, b) =>
          Number(isUnnamed(a.char_name)) - Number(isUnnamed(b.char_name)) ||
          String(a.char_name || "").localeCompare(String(b.char_name || ""), undefined, { numeric: true, sensitivity: "base" })
      : sort === "tracks"
      ? (a, b) => b.tracks_data.length - a.tracks_data.length
      : (a, b) => screen.get(b.char_id) - screen.get(a.char_id);
  return (items || []).slice().sort((a, b) => Number(isOther(a)) - Number(isOther(b)) || rank(a) - rank(b) || cmp(a, b));
}

/** track_id -> char_id of the character it belongs to */
export function ownerMap(items) {
  const m = new Map();
  (items || []).forEach((c) => (c.tracks_data || []).forEach((t) => m.set(t.track_id, c.char_id)));
  return m;
}

/**
 * tracks grouped by the character they belong to now, leaving out those already with `target`;
 * replaying these as move_tracks calls undoes a move or delete
 */
export function groupByOwner(ids, owners, target) {
  const g = new Map();
  (ids || []).forEach((id) => {
    const o = owners.get(id);
    if (o === undefined || o === target) return;
    if (!g.has(o)) g.set(o, []);
    g.get(o).push(id);
  });
  return Array.from(g, ([char_id, track_ids]) => ({ char_id, track_ids }));
}

/** the selection's characters: [char_id] in first-selected order */
export function selectionSources(sel, owners) {
  const out = [];
  sel.forEach((id) => {
    const o = owners.get(id);
    if (o !== undefined && !out.includes(o)) out.push(o);
  });
  return out;
}

/**
 * character list with `ids` moved to `toCharId` (DELETED: into the Others row, if the list has it): the local, optimistic version of
 * POST characters/move_tracks. Returns new objects only for the characters that change.
 */
export function moveTracks(items, ids, toCharId) {
  const want = new Set(ids);
  const moving = [];
  let next = (items || []).map((c) => {
    if (c.char_id === toCharId) return c;
    const keep = c.tracks_data.filter((t) => (want.has(t.track_id) ? !moving.push(t) : true));
    return keep.length === c.tracks_data.length ? c : { ...c, tracks_data: keep };
  });
  if (moving.length)
    next = next.map((c) => (c.char_id === toCharId ? { ...c, tracks_data: c.tracks_data.concat(moving).sort(byStart) } : c));
  return next;
}

/** thumbnail of a track (id, or the body-track file name in `thumbnail`); -1 = the "All Characters" picture */
export function thumbUrl(base, t) {
  if (t === -1 || t === null || t === undefined) return "/img/people.png";
  if (typeof t === "object") return thumbUrl(base, "thumbnail" in t && t.thumbnail ? t.thumbnail : t.track_id);
  return typeof t === "string" ? base + "/extended_body_track_thumbnails/" + t : base + "/tracks_thumbnails/" + t + ".jpg";
}
